/*
 * Copyright '2025' Dell Inc. or its subsidiaries. All Rights Reserved.
 */
import { BaseFeatureTogglesProvider } from "sirius-platform-support-library/shared/feature-toggles/providers/base-feature-toggles.provider";
import { FeatureToggle } from "sirius-platform-support-library/shared/feature-toggles/models/feature-toggle.model";
import { FeatureToggleRecord } from "./models/feature-toggle-record.model";
import { TdpFeatureTogglesProviderOptions } from "./tdp-feature-toggles-provider.options";
import { FeatureToggleMapper } from "./mappers/feature-toggle.mapper";
import { IFeatureTogglesStore } from "sirius-platform-support-library/shared/feature-toggles/providers/feature-toggles-store.interface";
import { environment } from "../../../environments/environments";

export const TdpFeatureTogglesProviderTypeName = "TdpFeatureTogglesProvider";

export class TdpFeatureTogglesProvider
  extends BaseFeatureTogglesProvider
  implements IFeatureTogglesStore
{
  public static readonly NAMESPACE = "tdp";

  private readonly window: Window;
  private readonly options: TdpFeatureTogglesProviderOptions;
  private readonly mapper: FeatureToggleMapper;

  public constructor(
    window: Window,
    mapper: FeatureToggleMapper,
    options: TdpFeatureTogglesProviderOptions
  ) {
    super(TdpFeatureTogglesProvider.NAMESPACE);
    this.window = window;
    this.mapper = mapper;
    this.options = options;
  }

  public async getFeatureToggles(): Promise<FeatureToggle[]> {
    const records = await this.getFeatureTogglesFromApi();
    return this.mapper.mapAll(records);
  }

  public async setFeatureToggle(code: string, enabled: boolean): Promise<void> {
    try {
      const releaseToggleUpdateEndpoint =
        environment.releaseToggleUpdateEndpoint + "/" + code;
      const releaseToggleUpdateBody = {
        name: code,
        active: enabled,
      };
      const response = await this.window.fetch(releaseToggleUpdateEndpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(releaseToggleUpdateBody),
        cache: "no-cache",
        mode: "cors",
        credentials: "include",
        referrerPolicy: "no-referrer",
      });

      if (response.status !== 200) {
        throw new Error(
          `Fetch failed with ${response.status} status code. Details: ${response.statusText}`
        );
      }

      this.refresh();
    } catch (e) {
      console.error(
        "An exception has been encountered while trying to set feature toggle",
        e
      );
      throw e;
    }
  }

  private async getFeatureTogglesFromApi(): Promise<FeatureToggleRecord[]> {
    try {
      const response = await this.window.fetch(this.options.endpoint, {
        method: "GET",
        headers: {
          accept: "application/json",
        },
        cache: "no-cache",
        mode: "cors",
        credentials: "include",
        referrerPolicy: "no-referrer",
      });

      if (response.status !== 200) {
        throw new Error(
          `Fetch failed with ${response.status} status code. Details: ${response.statusText}`
        );
      }

      const records = await response.json();
      if (!Array.isArray(records)) {
        throw new Error(
          `Received response is not an array containing feature toggles`
        );
      }

      return records;
    } catch (e) {
      console.error(
        "An exception has been encountered while trying to fetch feature toggles",
        e
      );
      return [];
    }
  }
}
